import { isValid } from "date-fns";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import type {
  AgeRange,
  AnnualIncome,
  Browser,
  DayOfWeek,
  DeliverDayOfWeekInput,
  DeliverKind,
  Device,
  DisplayPopupAgainStatus,
  DistributionStatus,
  Gender,
  ImportAction,
  MatchOp,
  Platform,
  PopupActionKind,
  PopupAnimationKind,
  PopupCloseButton,
  PopupCloseButtonPosition,
  PopupClosePopupByClickingOutside,
  PopupColorKind,
  PopupContentKind,
  PopupDisplayKind,
  PopupGroupDisplayTargetContentKind,
  PopupHrefTarget,
  PopupImportGroupInput,
  PopupImportImageInput,
  PopupImportPopupInput,
  PopupImportRowInput,
  PopupImportVerificationInput,
  PopupYdaTrigger,
} from "~/src/__relay_artifacts__/PopupImportContainerEnqueueMutation.graphql";

import {
  POPUP_IMPORT_ALLOWED_AGE_RANGE,
  POPUP_IMPORT_ALLOWED_ANNUAL_INCOME,
  POPUP_IMPORT_ALLOWED_BROWSER,
  POPUP_IMPORT_ALLOWED_DEVICE,
  POPUP_IMPORT_ALLOWED_GENDER,
  POPUP_IMPORT_ALLOWED_PLATFORM,
  filterGroupEnumValues,
  isCompleteEnumSelection,
} from "./popupImportGraphqlEnumCells";
import {
  type PopupImportSourceOption,
  resolveImportedSourceIds,
} from "./popupImportSourceOptions";
import type {
  PopupImportDeliverDayOfWeekRow,
  PopupImportImageForm,
  PopupImportRowValues,
} from "./validationSchema";

const DAY_OF_WEEK = new Set<DayOfWeek>([
  "SUN",
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SUT",
]);

const trim = (v: string | undefined | null): string =>
  (v ?? "").toString().trim();

/** CSV / form string cells that `buildPopup` parses with the same rules as `parseBool`. */
export function parsePopupImportCsvBoolString(raw: string): boolean {
  const t = trim(raw).toLowerCase();
  return t === "true" || t === "1" || t === "yes" || t === "t";
}

const parseBool = (raw: string): boolean => parsePopupImportCsvBoolString(raw);

const parseIntSafe = (raw: string, def: number): number => {
  const t = trim(raw);
  if (!t) return def;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : def;
};

const parseFloatSafe = (raw: string, def: number): number => {
  const t = trim(raw);
  if (!t) return def;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : def;
};

const emptyToNull = (v: string): string | null => {
  const t = trim(v);
  return t === "" ? null : t;
};

const ACTION_KINDS: PopupActionKind[] = [
  "DURATION",
  "SCROLL_RATE",
  "SCROLL_PIXEL",
  "EXIT_PAGE",
  "EXIT_AND_SCROLL_RATE_1",
  "EXIT_AND_SCROLL_RATE_2",
  "EXIT_AND_SCROLL_RATE_3",
  "SCROLL_RATE_RANGE",
  "CUSTOM_EVENT",
];

/** Rails enum integer codes for `action_type` (from `popup.rb`). */
const ACTION_KIND_NUMERIC_CODES: Record<number, PopupActionKind> = {
  1000: "DURATION",
  2000: "SCROLL_RATE",
  3000: "SCROLL_PIXEL",
  4000: "EXIT_PAGE",
  4001: "EXIT_AND_SCROLL_RATE_1",
  4002: "EXIT_AND_SCROLL_RATE_2",
  4003: "EXIT_AND_SCROLL_RATE_3",
  6000: "SCROLL_RATE_RANGE",
  9999: "CUSTOM_EVENT",
};

const DISPLAY_KINDS: PopupDisplayKind[] = [
  "CENTER_MODAL",
  "LEFT_MODAL",
  "RIGHT_MODAL",
  "TOP_MODAL",
  "BOTTOM_MODAL",
  "LEFT_BOTTOM_MODAL",
  "RIGHT_BOTTOM_MODAL",
  "CUSTOM_MODAL",
];

const COLOR_KINDS: PopupColorKind[] = [
  "DEFAULT",
  "WHITE",
  "GRAY",
  "BLACK",
  "RED",
  "BLUE",
  "GREEN",
  "YELLOW",
];

const ANIMATION_KINDS: PopupAnimationKind[] = [
  "NO_ANIMATION",
  "FROM_RIGHT",
  "FROM_LEFT",
  "FROM_TOP",
  "FROM_BOTTOM",
];

const YDA_TRIGGERS: PopupYdaTrigger[] = ["YDA_AFFI", "YDA_ALWAYS", "YDA_SHOW"];

/** Backend / CSV export codes (e.g. Rails `yda_trigger` enum values). */
const YDA_TRIGGER_NUMERIC_CODES: Record<number, PopupYdaTrigger> = {
  1000: "YDA_AFFI",
  2000: "YDA_ALWAYS",
  3000: "YDA_SHOW",
};

function actionKindFromJapaneseLabel(raw: string): PopupActionKind | null {
  const t = trim(raw);
  if (!t) return null;
  for (const opt of GraphQLEnums.PopupActionKind) {
    if (opt.label === t || opt.label.trim() === t)
      return opt.value as PopupActionKind;
    if (opt.value === t) return opt.value as PopupActionKind;
  }
  const tl = t.toLowerCase();
  for (const opt of GraphQLEnums.PopupActionKind) {
    if (opt.label.toLowerCase() === tl) return opt.value as PopupActionKind;
  }
  return null;
}

function mapActionKind(raw: string): PopupActionKind {
  const t = trim(raw);
  if (ACTION_KINDS.includes(t as PopupActionKind)) return t as PopupActionKind;
  // Handle lowercase Rails enum keys (e.g. "duration" → "DURATION")
  const upper = t.toUpperCase();
  if (ACTION_KINDS.includes(upper as PopupActionKind))
    return upper as PopupActionKind;
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n)) {
    const fromCode = ACTION_KIND_NUMERIC_CODES[n];
    if (fromCode) return fromCode;
    // Legacy: array-index fallback (0-based)
    if (n >= 0 && n < ACTION_KINDS.length)
      return ACTION_KINDS[n] as PopupActionKind;
  }
  const fromJp = actionKindFromJapaneseLabel(raw);
  if (fromJp) return fromJp;
  return "EXIT_PAGE";
}

/** CSV / UI labels → canonical enum values for Formik `actionKindRaw`. */
export function popupImportFormActionKindRawFromCsv(raw: string): string {
  const t = trim(raw);
  if (!t) return "";
  return mapActionKind(raw);
}

function mapDisplayKind(raw: string): PopupDisplayKind {
  const t = trim(raw);
  if (DISPLAY_KINDS.includes(t as PopupDisplayKind))
    return t as PopupDisplayKind;
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n) && n >= 0 && n < DISPLAY_KINDS.length)
    return DISPLAY_KINDS[n] as PopupDisplayKind;
  return "CENTER_MODAL";
}

function mapColorKind(raw: string): PopupColorKind {
  const t = trim(raw);
  if (COLOR_KINDS.includes(t as PopupColorKind)) return t as PopupColorKind;
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n) && n >= 0 && n < COLOR_KINDS.length)
    return COLOR_KINDS[n] as PopupColorKind;
  return "DEFAULT";
}

function mapAnimationKind(raw: string): PopupAnimationKind {
  const t = trim(raw);
  if (ANIMATION_KINDS.includes(t as PopupAnimationKind))
    return t as PopupAnimationKind;
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n) && n >= 0 && n < ANIMATION_KINDS.length)
    return ANIMATION_KINDS[n] as PopupAnimationKind;
  return "NO_ANIMATION";
}

/** CSV export values (e.g. `1000`, `_blank`, `disable`) → strings for Formik `*Raw` selects (same as `buildPopup`). */
export function popupImportFormColorKindRawFromCsv(raw: string): string {
  return mapColorKind(raw);
}

export function popupImportFormHrefTargetRawFromCsv(raw: string): string {
  return mapHrefTarget(raw);
}

export function popupImportFormDisplayKindRawFromCsv(raw: string): string {
  return mapDisplayKind(raw);
}

export function popupImportFormClosePopupByClickingOutsideRawFromCsv(
  raw: string
): string {
  return mapCloseOutside(raw);
}

export function popupImportFormAnimationKindRawFromCsv(raw: string): string {
  return mapAnimationKind(raw);
}

function mapYdaTrigger(raw: string): PopupYdaTrigger | null {
  const t = trim(raw);
  if (!t) return null;
  if (YDA_TRIGGERS.includes(t as PopupYdaTrigger)) return t as PopupYdaTrigger;
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n)) {
    const fromExportCode = YDA_TRIGGER_NUMERIC_CODES[n];
    if (fromExportCode) return fromExportCode;
    if (n >= 0 && n < YDA_TRIGGERS.length)
      return YDA_TRIGGERS[n] as PopupYdaTrigger;
  }
  return "YDA_ALWAYS";
}

/** CSV export numeric codes (`1000` / `2000` / `3000`), indices `0`–`2`, or enum names → Formik `ydaTriggerRaw`. */
export function popupImportFormYdaTriggerRawFromCsv(raw: string): string {
  const t = trim(raw);
  if (!t) return "";
  return mapYdaTrigger(t) ?? "";
}

function mapHrefTarget(raw: string): PopupHrefTarget {
  const u = trim(raw).toUpperCase();
  if (u === "BLANK" || u === "TOP") return u as PopupHrefTarget;
  const t = trim(raw).toLowerCase();
  if (t === "_blank" || t === "blank") return "BLANK";
  if (t === "_top" || t === "top") return "TOP";
  return "BLANK";
}

function mapCloseOutside(raw: string): PopupClosePopupByClickingOutside {
  const u = trim(raw).toUpperCase();
  if (u === "ENABLED" || u === "DISABLED")
    return u as PopupClosePopupByClickingOutside;
  const t = trim(raw).toLowerCase();
  if (t === "enable" || t === "enabled" || t === "true" || t === "1")
    return "ENABLED";
  if (t === "disable" || t === "disabled") return "DISABLED";
  return "DISABLED";
}

/** Same order as `schema.graphql` `PopupContentKind` and backend enum indices. */
const POPUP_CONTENT_KIND_BY_INDEX: PopupContentKind[] = [
  "IMAGE",
  "VIDEO",
  "TEXT",
  "HTML",
];

function contentKindFromJapaneseLabel(raw: string): PopupContentKind | null {
  const t = trim(raw);
  if (!t) return null;
  for (const opt of GraphQLEnums.PopupContentKind) {
    if (opt.label === t || opt.label.trim() === t)
      return opt.value as PopupContentKind;
    if (opt.value === t) return opt.value as PopupContentKind;
  }
  const tl = t.toLowerCase();
  for (const opt of GraphQLEnums.PopupContentKind) {
    if (opt.label.toLowerCase() === tl) return opt.value as PopupContentKind;
  }
  return null;
}

function mapContentKind(raw: string): PopupContentKind {
  const t = trim(raw);
  if (!t) return "IMAGE";
  const upper = t.toUpperCase();
  if (
    upper === "IMAGE" ||
    upper === "TEXT" ||
    upper === "HTML" ||
    upper === "VIDEO"
  ) {
    return upper as PopupContentKind;
  }
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n) && n >= 0 && n < POPUP_CONTENT_KIND_BY_INDEX.length) {
    return POPUP_CONTENT_KIND_BY_INDEX[n];
  }
  const fromJp = contentKindFromJapaneseLabel(raw);
  if (fromJp) return fromJp;
  return "IMAGE";
}

/** CSV / export labels and enum indices → canonical value for Formik `contentKindRaw`. */
export function popupImportFormContentKindRawFromCsv(raw: string): string {
  const t = trim(raw);
  if (!t) return "";
  return mapContentKind(raw);
}

export function mapDistributionStatus(raw: string): DistributionStatus | null {
  const t = trim(raw).toUpperCase();
  if (t === "ACTIVE" || t === "INACTIVE") return t as DistributionStatus;
  if (t === "1" || t === "true") return "ACTIVE";
  if (t === "0" || t === "false") return "INACTIVE";
  return "ACTIVE";
}

function mapDisplayPopupAgain(raw: string): DisplayPopupAgainStatus {
  const s = trim(raw);
  if (s === "有効") return "ACTIVE";
  if (s === "無効") return "INACTIVE";
  const u = s.toUpperCase();
  if (u === "ACTIVE" || u === "INACTIVE") return u as DisplayPopupAgainStatus;
  const t = s.toLowerCase();
  if (t === "active" || t === "enable" || t === "true" || t === "1")
    return "ACTIVE";
  return "INACTIVE";
}

/** CSV / form raw for 再表示ボタン — canonical `ACTIVE` | `INACTIVE` for selects + API. */
export function normalizePopupImportDisplayPopupAgain(
  raw: string
): DisplayPopupAgainStatus {
  return mapDisplayPopupAgain(raw);
}

/** Whether the 再表示ボタン column should show follow-up fields (e.g. 再表示ボタン用画像). */
export function isPopupImportDisplayPopupAgainActive(
  raw: string | undefined | null
): boolean {
  return mapDisplayPopupAgain(raw ?? "") === "ACTIVE";
}

function mapDeliverKind(raw: string): DeliverKind {
  const t = trim(raw).toUpperCase();
  if (t === "WEIGHT") return "WEIGHT";
  if (t === "2000") return "WEIGHT";
  if (t === "1000") return "EQUAL";
  const j = trim(raw);
  if (j.includes("配信比率")) return "WEIGHT";
  if (j.includes("均等")) return "EQUAL";
  return "EQUAL";
}

function dateFieldToIso8601(
  v: Date | string | null | undefined
): string | undefined {
  if (v == null) return undefined;
  if (v instanceof Date) {
    return isValid(v) ? v.toISOString() : undefined;
  }
  if (typeof v === "string") return emptyToNull(v) ?? undefined;
  return undefined;
}

function buildDeliverDayOfWeeksInput(
  rows: PopupImportDeliverDayOfWeekRow[]
): ReadonlyArray<DeliverDayOfWeekInput> | undefined {
  const mapped = rows
    .filter((r) => r.daysOfWeek?.length)
    .map((r) => {
      const daysOfWeek = r.daysOfWeek.filter((d): d is DayOfWeek =>
        DAY_OF_WEEK.has(d as DayOfWeek)
      );
      return {
        weekOfMonth: r.weekOfMonth ?? null,
        daysOfWeek,
      };
    })
    .filter((r) => r.daysOfWeek.length > 0);
  return mapped.length ? mapped : undefined;
}

function mapScrollRateRange(raw: string): string {
  const t = trim(raw);
  if (t.includes("~")) return t;
  return "0~0";
}

function mapCloseButtonType(raw: string): PopupCloseButton {
  const t = trim(raw).toUpperCase();
  if (
    t === "CLOSE_ICON_DEFAULT" ||
    t === "CLOSE_ICON_JP" ||
    t === "CLOSE_ICON_EN"
  )
    return t as PopupCloseButton;
  return "CLOSE_ICON_DEFAULT";
}

function mapCloseButtonPosition(raw: string): PopupCloseButtonPosition {
  const t = trim(raw).toUpperCase();
  if (["TOP_RIGHT", "TOP_LEFT", "BOTTOM_LEFT", "BOTTOM_RIGHT"].includes(t)) {
    return t as PopupCloseButtonPosition;
  }
  return "TOP_RIGHT";
}

function buildVerification(
  row: PopupImportRowValues
): PopupImportVerificationInput | null {
  const funnelId = emptyToNull(row.verificationFunnelId);
  const pageId = emptyToNull(row.verificationPageId);
  if (!funnelId && !pageId) return null;
  return { funnelId: funnelId ?? undefined, pageId: pageId ?? undefined };
}

/** Drops import-only `POPUP_IMPORT_SEGMENT_ALL_VALUE`; returns empty array for
 *  "all/unrestricted" so the backend clears existing restrictions on UPDATE. */
function segmentEnumsForMutation<T extends string>(
  values: string[] | undefined,
  allowed: ReadonlySet<string>
): T[] | undefined {
  if (values === undefined) return undefined;
  const filtered = filterGroupEnumValues(values, allowed) as T[];
  if (filtered.length === 0) return [] as unknown as T[];
  if (isCompleteEnumSelection(values, allowed)) return [] as unknown as T[];
  return filtered;
}

type VisitMatchType =
  | "EQUAL_MATCH_TYPE"
  | "OVER_MATCH_TYPE"
  | "UNDER_MATCH_TYPE";

function parseVisitCountCell(
  raw: string
): { count: number; matchType: VisitMatchType } | null {
  const t = trim(raw);
  if (!t) return null;
  const m = t.match(/^(==|>=|<=)\s*(\d+)/);
  if (m) {
    const count = Number.parseInt(m[2], 10);
    const op = m[1];
    const matchType: VisitMatchType =
      op === ">="
        ? "OVER_MATCH_TYPE"
        : op === "<="
        ? "UNDER_MATCH_TYPE"
        : "EQUAL_MATCH_TYPE";
    return { count, matchType };
  }
  const n = Number.parseInt(t, 10);
  if (Number.isFinite(n) && n > 0)
    return { count: n, matchType: "EQUAL_MATCH_TYPE" };
  return null;
}

function buildPopupGroup(
  row: PopupImportRowValues,
  sourceOptions?: ReadonlyArray<PopupImportSourceOption>
): PopupImportGroupInput {
  const g = row.group;

  const redisplayPopupTime = parseIntSafe(g.redisplayPopupTime, 0);
  const visitCountResult = parseVisitCountCell(g.visitCount);

  const scheduleIds = (g.scheduleIds ?? [])
    .map((id) => trim(id))
    .filter(Boolean);

  const sourceIds =
    sourceOptions && sourceOptions.length > 0
      ? resolveImportedSourceIds(g.sourceIds, [...sourceOptions])
          .map((id) => trim(id))
          .filter(Boolean)
      : (g.sourceIds ?? []).map((id) => trim(id)).filter(Boolean);
  const excludeSourceIds =
    sourceOptions && sourceOptions.length > 0
      ? resolveImportedSourceIds(g.excludeSourceIds, [...sourceOptions])
          .map((id) => trim(id))
          .filter(Boolean)
      : (g.excludeSourceIds ?? []).map((id) => trim(id)).filter(Boolean);

  const group: PopupImportGroupInput = {
    name: emptyToNull(g.name) ?? undefined,
    memo: emptyToNull(g.memo) ?? undefined,
    startDate: dateFieldToIso8601(g.startDate) ?? undefined,
    endDate: dateFieldToIso8601(g.endDate) ?? undefined,
    scheduleIds: scheduleIds.length ? scheduleIds : undefined,
    deliverDayOfWeeks: buildDeliverDayOfWeeksInput(g.deliverDayOfWeeks ?? []),
    redisplayPopupTime,
    sourceIds: sourceIds.length ? sourceIds : undefined,
    excludeSourceIds: excludeSourceIds.length ? excludeSourceIds : undefined,
    referrers:
      g.referrers.length > 0
        ? g.referrers.map((r) => ({
            referrer: r.referrer,
            matchOp: (r.matchOp || "EQUAL") as MatchOp,
          }))
        : undefined,
    excludeReferrers:
      g.excludeReferrers.length > 0
        ? g.excludeReferrers.map((r) => ({
            referrer: r.referrer,
            matchOp: (r.matchOp || "EQUAL") as MatchOp,
          }))
        : undefined,
    visitCount:
      visitCountResult !== null && visitCountResult.count > 0
        ? visitCountResult.count
        : undefined,
    visitMatchType:
      visitCountResult !== null && visitCountResult.count > 0
        ? visitCountResult.matchType
        : undefined,
    displayTargetContents:
      g.displayTargetContents.length > 0
        ? g.displayTargetContents.map((x) => ({
            targetKind: x.targetKind as PopupGroupDisplayTargetContentKind,
            targetValue: x.targetValue,
          }))
        : undefined,
    disableTargetElements:
      g.disableTargetElements.length > 0
        ? g.disableTargetElements.map((x) => ({
            targetKind: x.targetKind as PopupGroupDisplayTargetContentKind,
            targetValue: x.targetValue,
          }))
        : undefined,
    deliverTargetUrls:
      g.deliverTargetUrls.length > 0 ? g.deliverTargetUrls : undefined,
    deliverExcludeUrls:
      g.deliverExcludeUrls.length > 0 ? g.deliverExcludeUrls : undefined,
    targetCookies: g.targetCookies.length > 0 ? g.targetCookies : undefined,
    isMulti: parseBool(g.isMulti),
    deliverKind: mapDeliverKind(g.deliverKind),
    devices: segmentEnumsForMutation<Device>(
      g.devices,
      POPUP_IMPORT_ALLOWED_DEVICE
    ),
    platforms: segmentEnumsForMutation<Platform>(
      g.platforms,
      POPUP_IMPORT_ALLOWED_PLATFORM
    ),
    browsers: segmentEnumsForMutation<Browser>(
      g.browsers,
      POPUP_IMPORT_ALLOWED_BROWSER
    ),
    genders: segmentEnumsForMutation<Gender>(
      g.genders,
      POPUP_IMPORT_ALLOWED_GENDER
    ),
    ageRanges: segmentEnumsForMutation<AgeRange>(
      g.ageRanges,
      POPUP_IMPORT_ALLOWED_AGE_RANGE
    ),
    annualIncomes: segmentEnumsForMutation<AnnualIncome>(
      g.annualIncomes,
      POPUP_IMPORT_ALLOWED_ANNUAL_INCOME
    ),
  };

  return group;
}

function includePopupImageSlot(im: PopupImportImageForm): boolean {
  return (
    !!trim(im.imageUrl) ||
    !!trim(im.honeycombCode) ||
    !!trim(im.alt) ||
    !!trim(im.linkUrl)
  );
}

function buildPopupImages(row: PopupImportRowValues): PopupImportImageInput[] {
  return row.popupImages.filter(includePopupImageSlot).map((im) => ({
    imageUrl: emptyToNull(im.imageUrl) ?? undefined,
    honeycombCode: emptyToNull(im.honeycombCode) ?? undefined,
    alt: emptyToNull(im.alt) ?? undefined,
    linkUrl: emptyToNull(im.linkUrl) ?? undefined,
    isInheritQuery: im.isInheritQuery,
  }));
}

function buildPopup(row: PopupImportRowValues): PopupImportPopupInput {
  const p = row.popup;
  const isHiddenCloseButton = !p.isClose;
  const contentWidth = parseIntSafe(p.contentWidth, 450);
  const spContentWidth = parseIntSafe(p.spContentWidth, 0);
  const backgroundOpacity = parseFloatSafe(p.backgroundOpacity, 0.4);
  const hideByScrollPercentage = parseIntSafe(p.hideByScrollPercentageRaw, 0);
  const quitTiming = parseIntSafe(p.quitTiming, 0);
  const displayCountLimit = parseIntSafe(p.displayCountLimit, 0);
  const weight = Math.max(1, parseIntSafe(row.group.weightRaw, 1));
  const contentKind = mapContentKind(row.group.contentKindRaw) ?? "IMAGE";

  const popup: PopupImportPopupInput = {
    name: trim(p.name),
    actionKind: mapActionKind(p.actionKindRaw),
    animationKind: mapAnimationKind(p.animationKindRaw),
    backgroundOpacity,
    button: emptyToNull(p.button) ?? undefined,
    closePopupByClickingOutside: mapCloseOutside(
      p.closePopupByClickingOutsideRaw
    ),
    closeButtonPosition: mapCloseButtonPosition("TOP_RIGHT"),
    closeButtonType: mapCloseButtonType("CLOSE_ICON_DEFAULT"),
    closeButtonWidth: null,
    isHiddenCloseButton,
    colorKind: mapColorKind(p.colorKindRaw),
    contentWidth,
    spContentWidth: spContentWidth || null,
    displayKind: mapDisplayKind(p.displayKindRaw),
    description: emptyToNull(p.description) ?? undefined,
    displayOnTabFocus: parseBool(p.displayOnTabFocus),
    displayOnMouseOut: parseBool(p.displayOnMouseOut),
    enableTapWindow: parseBool(p.enableTapWindow),
    hideByScrollPercentage,
    hideByOnfocus: parseBool(p.hideByOnfocus),
    htmlContent:
      contentKind === "HTML"
        ? emptyToNull(p.customEvent) ?? undefined
        : undefined,
    hrefTarget: mapHrefTarget(p.hrefTargetRaw),
    honeycombCode: emptyToNull(p.honeycombCode) ?? undefined,
    isClose: true,
    isInheritQuery: parseBool(p.isInheritQuery),
    quitTiming,
    scrollRateRange: mapScrollRateRange(p.scrollRateRangeRaw),
    status: mapDistributionStatus(p.statusRaw) ?? undefined,
    displayCountLimit,
    displayPopupAgain: mapDisplayPopupAgain(p.displayPopupAgain),
    displayPopupAgainMessage:
      emptyToNull(p.displayPopupAgainMessage) ?? undefined,
    contentKind,
    kind: "B_PATTERN",
    timing: quitTiming,
    title: emptyToNull(p.title) ?? undefined,
    url: emptyToNull(p.url) ?? undefined,
    underlayerColor: undefined,
    windowColor: undefined,
    weight,
    xOffset: 0,
    xOffsetUnit: "px",
    yOffset: 0,
    yOffsetUnit: "px",
    ydaTrigger: mapYdaTrigger(p.ydaTriggerRaw),
    isDisplayWhenNoOperation: parseBool(p.isDisplayWhenNoOperation),
    customEvent:
      contentKind !== "HTML"
        ? emptyToNull(p.customEvent) ?? undefined
        : undefined,
    titleTextColor: undefined,
    buttonColor: undefined,
    buttonGradationColor: undefined,
    buttonTextColor: undefined,
  };

  return popup;
}

export function rowValuesToInput(
  row: PopupImportRowValues,
  sourceOptions?: ReadonlyArray<PopupImportSourceOption>
): PopupImportRowInput {
  const images = buildPopupImages(row);
  const verification = buildVerification(row);
  const popupGroup = buildPopupGroup(row, sourceOptions);
  const popup = buildPopup(row);

  return {
    action: row.action as ImportAction,
    popupId: emptyToNull(row.popupId) ?? undefined,
    popupGroupId:
      row.action === "CREATE"
        ? undefined
        : emptyToNull(row.popupGroupId) ?? undefined,
    verification: verification ?? undefined,
    popup,
    popupImages: images.length ? images : undefined,
    showPopupAgainImageUrl: isPopupImportDisplayPopupAgainActive(
      row.popup.displayPopupAgain
    )
      ? emptyToNull(row.popup.displayPopupAgainImage.imageUrl) ?? undefined
      : undefined,
    popupGroup,
  };
}
